import React, { useState, useEffect } from "react";
import "../styles/FeeEntryModal.css";
import API_URL from "../apiConfig";
import ReceiptDownload from "./ReceiptDownload"; // <-- adjusted to match your repo: ReceiptDownload.jsx is in the same folder

// Helper: get human-friendly year label
function getYearLabel(yr) {
  if (String(yr) === "1") return "1st Year GNM";
  if (String(yr) === "2") return "2nd Year GNM";
  if (String(yr) === "3") return "3rd Year GNM";
  return yr ? `${yr} Year` : "";
}

const initialState = {
  amount: "",
  status: "pending",
  payment_date: "",
  mode: "",
  reference: "",
  scholarship_amount: "",
  course_year: "",
  batch_id: "",
  session_year: "",
};

export default function FeeEntryModal({
  token,
  student,
  fee,
  editMode = false,
  onClose,
  onSave,
  user,
}) {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [lastSavedRecord, setLastSavedRecord] = useState(null); // store record after save so we can download

  const studentName =
    student?.name ||
    student?.student_name ||
    student?.full_name ||
    student?.first_name ||
    "Student";

  let batch_id = student?.batch_id || student?.batchId || "";
  let sessionYearDisplay = student?.session_year || student?.session || "";
  let session_year = typeof sessionYearDisplay === "string"
    ? sessionYearDisplay.match(/\d{4}-?\d{0,2}/)?.[0] || sessionYearDisplay
    : sessionYearDisplay;

  const annualFee = student?.annual_fee || "";

  useEffect(() => {
    if (editMode && fee) {
      let sessionYear = fee.session_year || session_year || "";
      setForm({
        amount: fee.amount !== undefined && fee.amount !== null ? String(fee.amount) : "",
        status: (fee.status || "pending").toLowerCase(),
        payment_date: fee.payment_date ? fee.payment_date.slice(0, 10) : "",
        mode: fee.mode || "",
        reference: fee.reference || "",
        scholarship_amount: fee.scholarship_amount !== undefined && fee.scholarship_amount !== null ? String(fee.scholarship_amount) : "",
        course_year: fee.course_year || student?.course_year || "",
        batch_id: fee.batch_id || batch_id || "",
        session_year: sessionYear || "",
      });
    } else {
      setForm({
        ...initialState,
        course_year: student?.course_year || "",
        batch_id: batch_id || "",
        session_year: session_year || "",
      });
    }
    // reset lastSavedRecord when student/fee changes
    setLastSavedRecord(null);
  }, [editMode, fee, student, batch_id, session_year]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "status" ? value.toLowerCase() : value,
    }));
  };

  const isValidNumberString = (val) => {
    if (val === "" || val === undefined || val === null) return false;
    const n = parseFloat(val);
    return Number.isFinite(n);
  };

  // HTML print template kept as a fallback only (we prefer jsPDF download)
  const printReceiptHtml = (rec, studentObj) => {
    const receiptNo = rec.receipt_no || rec.receipt || rec.receipt_no_text || "-";
    const amount = Number(rec.amount || rec.amount_paid || 0).toFixed(2);
    const scholarship = Number(rec.scholarship_amount || 0).toFixed(2);
    const refund = Number(rec.refund || 0).toFixed(2);
    const paymentDate = rec.payment_date ? (rec.payment_date.length >= 10 ? rec.payment_date.slice(0,10) : rec.payment_date) : "-";
    const mode = rec.mode || "-";
    const reference = rec.reference || "-";
    const studentNameLocal = studentObj?.name || `Student`;
    const rollNo = studentObj?.roll_no || studentObj?.roll || "-";
    const batch = studentObj?.batch || studentObj?.batch_name || studentObj?.batch_id || "-";
    const classLabel = rec.course_year ? `${rec.course_year} Year GNM` : (studentObj?.course_year ? `${studentObj.course_year} Year GNM` : "-");

    return `
      <html>
        <head>
          <title>Receipt ${receiptNo}</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; color: #222; padding: 20px; }
            .header { text-align:center; margin-bottom:12px; }
            .header h2 { margin:0; }
            .meta { margin-bottom:12px; text-align:center; font-size:14px; }
            table { width:100%; border-collapse: collapse; margin-top: 8px; }
            th, td { padding:8px 10px; border:1px solid #999; text-align:left; }
            th { background:#f0f7ff; }
            .right { text-align:right; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Sarojini Group of Institutes</h2>
            <div class="meta"><strong>Receipt:</strong> ${receiptNo} &nbsp; | &nbsp; <strong>Date:</strong> ${paymentDate}</div>
          </div>

          <div style="margin-bottom:8px;">
            <strong>Student:</strong> ${studentNameLocal} <br/>
            <strong>Roll No:</strong> ${rollNo} &nbsp; | &nbsp;
            <strong>Batch:</strong> ${batch} &nbsp; | &nbsp;
            <strong>Class:</strong> ${classLabel}
          </div>

          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th class="right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Tuition / Payment</td>
                <td class="right">${amount}</td>
              </tr>
              <tr>
                <td>Scholarship</td>
                <td class="right">${scholarship}</td>
              </tr>
              <tr>
                <td>Refund</td>
                <td class="right">${refund}</td>
              </tr>
              <tr>
                <td>Mode / Reference</td>
                <td class="right">${mode} / ${reference}</td>
              </tr>
            </tbody>
          </table>

          <div style="margin-top:18px; font-size:12px; color:#444;">
            This is a system generated receipt. For any queries contact the accounts office.
          </div>
        </body>
      </html>
    `;
  };

  // Fallback: if jsPDF fails we may open a print window (will be blocked in many browsers)
  const openPrintWindow = (html) => {
    const w = window.open("", "_blank", "noopener,noreferrer");
    if (!w) {
      alert("Unable to open print window — please allow popups for this site.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    setTimeout(() => {
      w.focus();
      w.print();
    }, 350);
  };

  // Fetch saved fee records for this student and find the matching fee row.
  // Then attempt to download PDF using ReceiptDownload.generatePDF(match).
  // If jsPDF fails we'll fall back to openPrintWindow as last resort.
  const fetchAndDownloadForFeeId = async (feeId) => {
    try {
      const url = `${API_URL.endsWith('/api') ? API_URL : API_URL + '/api'}/fees/records/${student?.id || student?.student_id || student?._id || ""}`;
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) return;
      const recs = await r.json();
      if (!Array.isArray(recs) || recs.length === 0) return;

      let match = null;
      if (feeId) {
        match = recs.find(x => (x.fee_id && Number(x.fee_id) === Number(feeId)));
      }
      if (!match) {
        match = recs.find(x => {
          const pd = x.payment_date ? x.payment_date.slice(0,10) : "";
          return pd === (form.payment_date ? form.payment_date : "") && String(x.reference || "") === String(form.reference || "");
        });
      }
      if (!match && recs.length) match = recs[0];

      if (match) {
        setLastSavedRecord(match);
        try {
          // primary behavior: generate and save PDF (no new tab)
          ReceiptDownload.generatePDF(match);
        } catch (err) {
          // fallback: open print HTML (may be blocked by popup blockers)
          console.error("ReceiptDownload failed, falling back to openPrintWindow", err);
          const html = printReceiptHtml(match, student);
          openPrintWindow(html);
        }
      }
    } catch (err) {
      console.error("fetchAndDownloadForFeeId error", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const studentId = student?.student_id || student?.id || student?._id;

    const amountVal = isValidNumberString(form.amount) ? parseFloat(form.amount) : NaN;
    const scholarshipVal = isValidNumberString(form.scholarship_amount) ? parseFloat(form.scholarship_amount) : NaN;

    const tuitionFilled = !isNaN(amountVal) && amountVal > 0;
    const scholarshipFilled = !isNaN(scholarshipVal) && scholarshipVal > 0;

    if (
      !studentId ||
      !form.batch_id ||
      !form.session_year ||
      !form.course_year ||
      !(tuitionFilled || scholarshipFilled) ||
      isNaN(Number(form.course_year)) ||
      !form.payment_date ||
      !form.mode
    ) {
      setMessage("Required fields missing or invalid. Ensure amount or scholarship is provided and course year, batch, session, payment date and mode are set.");
      setLoading(false);
      return;
    }

    if (editMode && user?.role !== "admin") {
      setMessage("You do not have permission to edit fee.");
      setLoading(false);
      return;
    }
    if (!editMode && !["admin", "principal"].includes(user?.role)) {
      setMessage("You do not have permission to add fee.");
      setLoading(false);
      return;
    }

    let paymentDate = form.payment_date;
    if (paymentDate && paymentDate.includes("/")) {
      const [dd, mm, yyyy] = paymentDate.split("/");
      paymentDate = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    }

    try {
      let url, method, body;

      const payload = {
        student_id: studentId,
        batch_id: form.batch_id,
        session_year: form.session_year,
        course_year: Number(form.course_year),
        // send decimals rounded to 2 dp
        amount: !isNaN(amountVal) ? parseFloat(amountVal.toFixed(2)) : 0,
        amount_paid: tuitionFilled && form.status === "paid" ? parseFloat(amountVal.toFixed(2)) : 0,
        scholarship_amount: !isNaN(scholarshipVal) ? parseFloat(scholarshipVal.toFixed(2)) : 0,
        due_date: null,
        status: (form.status || "pending").toLowerCase(),
        course_id: 1,
        payment_date: paymentDate,
        mode: form.mode,
        reference: form.reference,
      };

      if (editMode && fee) {
        url = `${API_URL.endsWith('/api') ? API_URL : API_URL + '/api'}/fees/update/${fee.id || fee.fee_id}`;
        method = "PUT";
        body = JSON.stringify(payload);
      } else {
        url = `${API_URL.endsWith('/api') ? API_URL : API_URL + '/api'}/fees/add`;
        method = "POST";
        body = JSON.stringify(payload);
      }

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body,
      });
      const data = await res.json();
      if (data.error) {
        setMessage(data.error || "Failed to save fee record.");
      } else {
        setMessage(editMode ? "Fee record updated successfully!" : "Fee record added successfully!");
        // call onSave to let parent refresh
        if (onSave) onSave();

        // If created (POST), backend should have inserted fee and income; attempt to locate and download receipt:
        const createdFeeId = data.fee_id || data.insertId || data.feeId || data.id || null;
        if (!editMode && createdFeeId) {
          // fetch the created record and download receipt (no new tab)
          await fetchAndDownloadForFeeId(createdFeeId);
        } else if (!editMode && !createdFeeId) {
          // If backend didn't return id, still try to fetch most recent record for this student and download if principal/admin
          if (user?.role === "principal" || user?.role === "admin") {
            await fetchAndDownloadForFeeId(null);
          }
        }

        // Close modal for admin/principal after download attempt
        if (onClose) onClose();
      }
    } catch (err) {
      console.error(err);
      setMessage("Failed to save fee record.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="feeentry-modal-bg">
      <div className="feeentry-modal">
        <form className="feeentry-form" onSubmit={handleSubmit}>
          <h3>
            {editMode
              ? `Edit Fee for ${studentName}`
              : `Add Fee for ${studentName}`}
          </h3>
          <div className="feeentry-field">
            <label>Annual Fee (read only)</label>
            <input value={annualFee} readOnly />
          </div>
          <div className="feeentry-field">
            <label>Amount (₹)</label>
            <input
              type="number"
              name="amount"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={handleChange}
              placeholder="Enter fee amount"
            />
          </div>
          <div className="feeentry-field">
            <label>Scholarship Amount / Refund</label>
            <input
              type="number"
              name="scholarship_amount"
              step="0.01"
              min="0"
              value={form.scholarship_amount}
              onChange={handleChange}
              placeholder="If any refund or scholarship"
            />
          </div>
          <div className="feeentry-field">
            <label>Status</label>
            <select name="status" value={form.status} onChange={handleChange}>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="processing">Processing</option>
              <option value="refunded">Refunded</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div className="feeentry-field">
            <label>Payment Date</label>
            <input
              type="date"
              name="payment_date"
              value={form.payment_date}
              onChange={handleChange}
              required
            />
          </div>
          <div className="feeentry-field">
            <label>Mode of Payment</label>
            <select name="mode" value={form.mode} onChange={handleChange} required>
              <option value="">Select Mode</option>
              <option value="Cash">Cash</option>
              <option value="Cheque">Cheque</option>
              <option value="UPI">UPI</option>
              <option value="NEFT">NEFT</option>
              <option value="Card">Card</option>
              <option value="Bank">Bank</option>
            </select>
          </div>
          <div className="feeentry-field">
            <label>Transaction Details/ID</label>
            <input
              type="text"
              name="reference"
              value={form.reference}
              onChange={handleChange}
              placeholder="Cheque/UPI/NEFT/Ref No."
            />
          </div>
          <div className="feeentry-field">
            <label>Class (Year)</label>
            <input
              value={getYearLabel(form.course_year)}
              readOnly
            />
          </div>
          {message && (
            <div className={`feeentry-message ${String(message).toLowerCase().includes("success") ? "success" : "error"}`}>
              {message}
            </div>
          )}
          <div className="feeentry-actions">
            <button
              className="student-btn"
              type="submit"
              disabled={
                loading ||
                (editMode && user?.role !== "admin") ||
                (!editMode && !["admin", "principal"].includes(user?.role))
              }
            >
              {loading
                ? (editMode ? "Updating..." : "Saving...")
                : (editMode ? "Update Fee" : "Add Fee")}
            </button>

            {/* Download Receipt (no new tab) */}
            {(lastSavedRecord && (user?.role === "principal" || user?.role === "admin")) && (
              <button
                className="student-btn"
                type="button"
                onClick={() => {
                  try {
                    ReceiptDownload.generatePDF(lastSavedRecord);
                  } catch (err) {
                    console.error(err);
                    alert("Unable to generate/download receipt.");
                  }
                }}
                style={{ marginLeft: 8, background: "#3478f6", color: "#fff" }}
              >
                Download Receipt
              </button>
            )}

            <button className="student-btn student-delete" type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}